import React, { useEffect, useRef } from 'react';
import { CompPanelHeader,
    useModePanelStyle,
    ModeTheme } from '@moderepo/mode-app-components';
import { makeStyles, Paper, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { AggregatedData } from './ui-components/EntityPieChartViewUIComponent';

const useStyle = makeStyles((theme: Theme) => {
    return {
        panelHeader: {
        },
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
        },
        pieChartDiv: {
            width : '100%',
            height: 500,
        },
    };
});

interface EntityPieChartContainerProps {
    readonly title?: string;
    readonly icon?: string | undefined;
    readonly chartIdPrefix: string;
    // readonly aggregation?: TimeSeriesAggregation | undefined;

    readonly strokeWidth?: number;
    readonly innerRadius?: number;
    readonly borderColor?: string;
    readonly chartData?: AggregatedData[] | undefined;
}

/**
 * This Component is used for displaying the Entity Pie Chart
 */
export const EntityPieChartContainer: React.FC<EntityPieChartContainerProps> = (props: EntityPieChartContainerProps) => {

    const classes = useStyle();
    const theme = useTheme() as ModeTheme;

    const chartRef = useRef<am4charts.PieChart>();
    const chartIdRef = useRef<string>();
    const chartDataRef = useRef<readonly AggregatedData[]>();

    const panelClasses = useModePanelStyle();
    const pieChartUniqueId = `${props.chartIdPrefix}-EntityPieChartContainer`;

    // This useEffect is used for cleaning up Chart when component unmounted
    useEffect(() => {
        // Return cleanup function
        return () => {
            const chart = chartRef.current as am4charts.PieChart;
            if (chart && !chart.isDisposed()) {
                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }
        };

    }, []);

    // This useEffect to create the PieChart + add data from the component
    useEffect(() => {
        let chart = chartRef.current as am4charts.PieChart;

        // We will need to re-create chart if chartId changed or chartData changed
        const createNewChart: boolean = chartIdRef.current !== props.chartIdPrefix || chartDataRef.current !== props.chartData;

        if (!chart || createNewChart) {
            if (chart && !chart.isDisposed()) {
                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }
            // create pie chart
            chart = am4core.create(pieChartUniqueId, am4charts.PieChart);
            chartRef.current = chart;
        }

        if (!props.chartData) {
            return;
        }

        // Add data
        chart.data = props.chartData;

        // chart details
        chartIdRef.current = props.chartIdPrefix;
        chartDataRef.current = props.chartData;
        const pieSeries = chart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = 'value';
        pieSeries.dataFields.category = 'displayMetricName';
        pieSeries.slices.template.stroke = am4core.color(props.borderColor ?? theme.palette.text.primary);
        pieSeries.slices.template.strokeWidth = props.strokeWidth ?? 0;
        pieSeries.slices.template.strokeOpacity = 1;
        pieSeries.slices.template.innerRadius = am4core.percent(props.innerRadius ?? 0);
        pieSeries.labels.template.fill = am4core.color(theme.palette.text.primary);
        pieSeries.ticks.template.stroke = am4core.color(theme.palette.text.primary);

        const modeColorSet = new am4core.ColorSet();
        modeColorSet.list = theme.palette.chart.series.map((modeColor) => {
            return am4core.color(modeColor.color);
        });
        pieSeries.colors = modeColorSet;

    }, [props.title, props.borderColor, props.innerRadius, props.strokeWidth,
        props.chartData, theme.palette.chart.series, theme.palette.text.primary, pieChartUniqueId, props.chartIdPrefix]);

    return (
        <Paper elevation={2} className={clsx(panelClasses.root)}>
            <CompPanelHeader
                headerClassName={clsx(panelClasses.panelHeader, classes.panelHeader)}
                borderlessHeader
                title={props.title}
                icon={props.icon ?? 'pie_chart'}
            />
            <div id={pieChartUniqueId} className={classes.pieChartDiv} />
        </Paper>
    );
};
